import { CustomError, logError } from './sentry';
import { getUrlParameter } from './window';
import { getObj } from './localStorage';

const isProductionDomain = () => {
  try {
    const host = window.location.hostname;
    return host.endsWith('.klikkie.com') && !host.startsWith('dev.');
  } catch {
    return false;
  }
};

export const getDevPrefix = () => {
  // Safety guard: production domains always use production APIs,
  // even if the bundle was accidentally built with KLIKKIE_ENV=dev
  if (isProductionDomain()) return '';

  /* eslint-disable no-undef */
  if (typeof __KLIKKIE_ENV__ !== 'undefined') {
    return __KLIKKIE_ENV__ === 'prod' ? '' : 'dev.';
  }
  /* eslint-enable no-undef */
  const env = getUrlParameter('env') || getObj('env').env;
  return env === 'production' || env === 'prod' || env === 'localProd' ? '' : 'dev.';
};

// KL-13836 — Safari race fix. The previous `onAuthStateChanged`-wait
// pattern unsubscribed on the FIRST emit, which on Safari/ITP lands
// with `auth.currentUser === null` while IndexedDB persistence is
// still restoring → request shipped unauthenticated → 401 cascade.
//
// `auth.authStateReady()` (firebase JS SDK ≥ 10.7) resolves only after
// the SDK has settled the initial auth state — either restored a
// persisted user or determined there is none. No race, no first-emit
// heuristic, no 10-second timeout to special-case.
//
// firebase ^12.13.0 in this repo exports `authStateReady` on the
// Auth object (see node_modules/@firebase/auth/dist/auth-public.d.ts).
export const getToken = async () => {
  try {
    const { getAuth } = await import(/* webpackChunkName: "firebase-auth" */ 'firebase/auth');
    const auth = getAuth();
    // Belt-and-braces: bound `authStateReady` with a 10s timeout.
    // firebase-js-sdk issues #7888 and #8019 document a Safari long-tail
    // (~1% of cold loads) where IndexedDB persistence itself hangs and
    // both `authStateReady` and the underlying `onAuthStateChanged`
    // never fire. Without a timeout, getToken would block every
    // authenticated request forever. When the timeout trips we log via
    // Sentry so we can measure how often it hits prod users, then fall
    // through to read `currentUser` (which may have populated even if
    // the wait promise never resolved) and ship the request without a
    // token if not — the server's 401 reads as "signed out".
    const TIMEOUT_MS = 10000;
    try {
      await Promise.race([
        auth.authStateReady(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('authStateReady timeout')), TIMEOUT_MS)
        ),
      ]);
    } catch (err) {
      logError(new CustomError('authStateReady timeout', { cause: err }), {
        timeoutMs: TIMEOUT_MS,
        hasCurrentUser: !!auth.currentUser,
      });
    }
    const user = auth.currentUser;
    if (!user) {
      // No logged-in user; we purposely skip creating an anonymous session.
      return null;
    }
    return await user.getIdToken();
  } catch (error) {
    return null;
  }
};

const MAX_RETRIES = 3;
const INITIAL_DELAY = 500;

const statusCodes = {
  badRequest: 400,
  unauthorized: 401,
  forbidden: 403,
  methodNotAllowed: 405,
  notAcceptable: 406,
  proxyAuthenticationRequired: 407,
  lengthRequired: 411,
  preconditionFailed: 412,
  payloadTooLarge: 413,
  uriTooLong: 414,
  unsupportedMediaType: 415,
  rangeNotSatisfiable: 416,
  expectationFailed: 417,
  imATeapot: 418,
  upgradeRequired: 426,
  unavailableForLegalReasons: 451,
  notFound: 404,
  gone: 410,
  unprocessableEntity: 422,
};

const fatalStatuses = [
  statusCodes.badRequest,
  statusCodes.unauthorized,
  statusCodes.forbidden,
  statusCodes.methodNotAllowed,
  statusCodes.notAcceptable,
  statusCodes.proxyAuthenticationRequired,
  statusCodes.lengthRequired,
  statusCodes.preconditionFailed,
  statusCodes.payloadTooLarge,
  statusCodes.uriTooLong,
  statusCodes.unsupportedMediaType,
  statusCodes.rangeNotSatisfiable,
  statusCodes.expectationFailed,
  statusCodes.imATeapot,
  statusCodes.upgradeRequired,
  statusCodes.unavailableForLegalReasons,
];

const finalStatuses = [
  ...fatalStatuses,
  statusCodes.notFound,
  statusCodes.gone,
  statusCodes.unprocessableEntity,
];

const isFatalError = (status) => fatalStatuses.includes(status);
const isFinalStatus = (status) => finalStatuses.includes(status);

const handleErrorResponse = async (response) => {
  if (!response.ok) {
    const { code, message } = await response
      .json()
      .catch(() => ({ code: 'Unknown', message: 'Unknown' }));
    const error = new Error(message);
    error.status = response.status;
    error.code = code;
    throw error;
  }
  return response;
};

const exponentialBackoff = async (makeRequest, retries = MAX_RETRIES, delay = INITIAL_DELAY) => {
  try {
    const result = await makeRequest();
    return result;
  } catch (error) {
    if (retries === 1 || isFinalStatus(error?.status)) {
      const level = isFatalError(error?.status) ? 'error' : 'warning';
      const customError = new CustomError(error?.message || error, level);
      // Preserve the error code from the API response
      if (error?.code) {
        customError.code = error.code;
      }
      // Preserve HTTP status so callers can branch on specific failures (e.g. 404 upsert fallback)
      if (error?.status) {
        customError.status = error.status;
      }
      throw customError;
    } else {
      // Otherwise, wait the delay time and try again.
      console.log(`Received ${error?.status} error, retrying in ${delay}ms...`);
      await new Promise((resolve) => {
        setTimeout(resolve, delay);
      });
      return exponentialBackoff(makeRequest, retries - 1, delay * 2);
    }
  }
};

/**
 * KL-14130: Merges an orphaned anonymous Firebase UID's data (orders, cart,
 * curate sessions) into a registered user UID via the klikkie-api endpoint.
 * Server-side handler (KL-13217) is idempotent. Mirrors klikkie-agent's
 * services/api/api.auth.ts:mergeAnonymousUser.
 *
 * The registered user's own ID token authorizes the request (the API route
 * uses `authMultiple`); the snapshotted anonymous ID token is sent in the body
 * so the server can verify ownership of the anon UID's data before transfer.
 */
export const mergeAnonymousUser = async (userId, anonymousIdToken) => {
  const uri = `https://${getDevPrefix()}api.klikkie.com/users/${userId}/mergeAnonymous`;
  return apiCall(uri, 'POST', { anonymousIdToken }, true);
};

// Durable anonymous-session continuity via a first-party HttpOnly cookie.
//
// Firebase's own client persistence (IndexedDB) is wiped in in-app WKWebViews
// and under iOS Safari ITP, so the funnel mints a fresh anonymous uid on every
// page. An HttpOnly `kl_anon` session cookie set by the API survives ITP (it's
// not script-readable, and api↔www are same-site under klikkie.com), letting us
// re-sign-in the SAME uid before minting. These calls use `credentials:'include'`
// so the browser sends/stores the cookie cross-origin (API CORS already allows
// credentials + reflects origin).
const anonSessionUri = path => `https://${getDevPrefix()}api.klikkie.com/auth/anon-session${path}`;

/**
 * Persist (roll forward) the session cookie for the current anonymous idToken.
 * `keepalive` so the request — and its Set-Cookie — completes even when the
 * funnel navigates to the next page before this resolves (the cookie-save would
 * otherwise be cut off on fast auto-advancing flows, leaving no cookie to
 * restore from on the next page).
 */
export const saveAnonSessionCookie = async accessToken => {
  await fetch(anonSessionUri(''), {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessToken }),
  });
};

/** Exchange the HttpOnly cookie for a custom token to re-sign-in the same uid, or null. */
export const restoreAnonSessionCookie = async () => {
  const res = await fetch(anonSessionUri('/restore'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) return null;
  const json = await res.json().catch(() => null);
  return json?.data?.customToken ?? json?.customToken ?? null;
};

/** Expire the session cookie (on explicit sign-out / once registered). `keepalive`
 *  so logout's clear isn't cut off by the post-logout navigation. */
export const clearAnonSessionCookie = async () => {
  await fetch(anonSessionUri('/clear'), { method: 'POST', credentials: 'include', keepalive: true }).catch(() => {});
};

/**
 * KL-14196: Server-side anon-merge claim. Stashes the anonymous Firebase user's
 * intent server-side so the merge survives a localStorage wipe (Instagram
 * WKWebView purge, iOS Safari ITP, Gmail in-app browser, OAuth redirect).
 * Called BEFORE any sign-in operation, while the caller is still the anonymous
 * Firebase user (so the bearer token authorises as 'anonymous').
 *
 * Returns the opaque claimToken (UUID, 7d TTL) the caller stashes for later
 * consume. The server derives anonUid from the bearer token.
 */
export const createAnonMergeClaim = async ({ photobookId, cart } = {}) => {
  const uri = `https://${getDevPrefix()}api.klikkie.com/claims`;
  return apiCall(uri, 'POST', { photobookId, cart }, true);
};

/**
 * KL-14196: Redeem an anon-merge claim. The bearer is the new registered
 * user's ID token; the server validates the claim, runs
 * UsersService.handleMergeAnonymous(registeredUid, anonUid) server-side, and
 * marks the claim consumed (one-shot). Returns `{ anonUid, photobookId }`.
 */
export const consumeAnonMergeClaim = async (claimToken) => {
  const uri = `https://${getDevPrefix()}api.klikkie.com/claims/${encodeURIComponent(claimToken)}/consume`;
  return apiCall(uri, 'POST', {}, true);
};

const sendRequest = async (uri, method, body, token) => {
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  if (token) {
    options.headers.Authorization = `Bearer ${token}`;
  }

  try {
    const finalResponse = await exponentialBackoff(async () => {
      const response = await fetch(uri, options);
      if (!response.ok) {
        await handleErrorResponse(response);
      }

      // Handle empty responses (204 No Content or empty body)
      const text = await response.text();
      const json = text ? JSON.parse(text) : { data: null };
      return json;
    });

    return finalResponse;
  } catch (error) {
    if (isFatalError(error.status)) {
      logError('Failed to call api', {
        error,
        uri,
        method,
        body,
      });
    } else {
      console.warn(error, {
        error,
        uri,
        method,
        body,
      });
    }
    throw error;
  }
};

export const apiCall = async (uri, method, body, useToken = false) => {
  console.log('Calling api...', {
    uri,
    method,
    body,
    useToken,
  });
  return sendRequest(uri, method, body, useToken ? await getToken() : null);
};

/**
 * KL-15471: a request that must carry this ID token. Order writes use it: the
 * orders API checks the userId in the body against the token, so the caller
 * passes the token of the same Firebase user it named. apiCall(…, true) looks
 * the token up and sends the request without one when there is none; this
 * never sends it without.
 */
export const apiCallWithToken = async (uri, method, body, idToken) => {
  if (!idToken) {
    throw new CustomError(`No ID token for ${method} ${uri}; request not sent`, 'error');
  }
  console.log('Calling api with ID token...', { uri, method, body });
  return sendRequest(uri, method, body, idToken);
};
